import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import RatingTableSeedData from '@/components/admin/RatingTableSeedData';

export default function RatingTables() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [formData, setFormData] = useState({
    benefit_type: 'gap',
    name: '',
    rating_method: 'age_banded',
    rates: {},
    effective_date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      const data = await api.entities.RatingTable.list('-created_at');
      setTables(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTable(null);
    setFormData({
      benefit_type: 'gap',
      name: '',
      rating_method: 'age_banded',
      rates: {},
      effective_date: format(new Date(), 'yyyy-MM-dd'),
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (table) => {
    setEditingTable(table);
    setFormData(table);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingTable) {
        await api.entities.RatingTable.update(editingTable.id, formData);
        toast.success('Rating table updated');
      } else {
        await api.entities.RatingTable.create(formData);
        toast.success('Rating table created');
      }
      setShowModal(false);
      loadTables();
    } catch (err) {
      toast.error('Failed to save rating table');
    }
  };

  const ratingTemplates = {
    age_banded: { 39: 10.50, 49: 15.25, 59: 22.00, 69: 35.50 },
    age_gender: { M: { 39: 10.50, 49: 15.25, 59: 22.00, 69: 35.50 }, F: { 39: 9.50, 49: 14.25, 59: 21.00, 69: 34.50 } },
    geographic: { zone1: 1.0, zone2: 1.2, zone3: 1.5 },
    tobacco: { non_tobacco: 1.0, tobacco: 1.5 },
    flat: { base_rate: 25.00 }
  };

  const loadTemplate = () => {
    setFormData({ ...formData, rates: ratingTemplates[formData.rating_method] || {} });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Rating Tables</h1>
          <p className="text-slate-500 mt-1">Manage benefit premium rating structures</p>
        </div>
        <Button onClick={handleAdd} className="self-start sm:self-auto bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-2" />
          Add Rating Table
        </Button>
      </div>

      {tables.length === 0 && !loading && <RatingTableSeedData onComplete={loadTables} />}

      <Tabs defaultValue="all" className="w-full">
        <div className="w-full overflow-x-auto">
          <TabsList className="flex w-max min-w-full justify-start h-auto py-1">
            <TabsTrigger value="all" className="flex-shrink-0 whitespace-nowrap">All Tables</TabsTrigger>
            <TabsTrigger value="gap" className="flex-shrink-0 whitespace-nowrap">GAP</TabsTrigger>
            <TabsTrigger value="hsp" className="flex-shrink-0 whitespace-nowrap">Hospital</TabsTrigger>
            <TabsTrigger value="dental" className="flex-shrink-0 whitespace-nowrap">Dental</TabsTrigger>
            <TabsTrigger value="life" className="flex-shrink-0 whitespace-nowrap">Life</TabsTrigger>
            <TabsTrigger value="critical" className="flex-shrink-0 whitespace-nowrap">Critical</TabsTrigger>
          </TabsList>
        </div>

        {['all', 'gap', 'hsp', 'dental', 'life', 'critical'].map(tab => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            {tables
              .filter(t => tab === 'all' || t.benefit_type === tab)
              .map(table => (
                <Card key={table.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {table.name}
                          <Badge variant="outline">{table.benefit_type.toUpperCase()}</Badge>
                          <Badge>{table.rating_method.replace('_', ' ')}</Badge>
                        </CardTitle>
                        <p className="text-sm text-slate-500 mt-1">
                          Effective: {format(new Date(table.effective_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(table)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    {table.rating_method === 'age_banded' && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Age Band</TableHead>
                            <TableHead>Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(table.rates || {}).map(([age, rate]) => (
                            <TableRow key={age}>
                              <TableCell>Up to {age}</TableCell>
                              <TableCell>${parseFloat(rate).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {table.rating_method === 'age_gender' && (
                      <div className="space-y-4">
                        {Object.entries(table.rates || {}).map(([gender, rates]) => (
                          <div key={gender}>
                            <h4 className="font-medium text-sm mb-2">{gender === 'M' ? 'Male' : 'Female'} Rates</h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Age Band</TableHead>
                                  <TableHead>Rate</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {Object.entries(rates || {}).map(([age, rate]) => (
                                  <TableRow key={age}>
                                    <TableCell>Up to {age}</TableCell>
                                    <TableCell>${parseFloat(rate).toFixed(2)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ))}
                      </div>
                    )}

                    {table.rating_method === 'geographic' && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Zone</TableHead>
                            <TableHead>Multiplier/Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(table.rates || {}).map(([zone, rate]) => (
                            <TableRow key={zone}>
                              <TableCell className="capitalize">{zone.replace('_', ' ')}</TableCell>
                              <TableCell>{typeof rate === 'number' && rate < 10 ? `${rate}x` : `$${parseFloat(rate).toFixed(2)}`}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {table.rating_method === 'flat' && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Coverage Tier</TableHead>
                            <TableHead>Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(table.rates || {}).map(([tier, rate]) => (
                            <TableRow key={tier}>
                              <TableCell className="capitalize">{tier.replace(/_/g, ' ')}</TableCell>
                              <TableCell>${parseFloat(rate).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {table.notes && (
                      <p className="text-sm text-slate-600 mt-3 pt-3 border-t">{table.notes}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTable ? 'Edit Rating Table' : 'Add Rating Table'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Benefit Type</Label>
                <Select value={formData.benefit_type} onValueChange={(v) => setFormData({...formData, benefit_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gap">GAP</SelectItem>
                    <SelectItem value="hsp">Hospital Indemnity</SelectItem>
                    <SelectItem value="dental">Dental</SelectItem>
                    <SelectItem value="life">Life</SelectItem>
                    <SelectItem value="critical">Critical Illness</SelectItem>
                    <SelectItem value="accident">Accident</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Rating Method</Label>
                <Select value={formData.rating_method} onValueChange={(v) => setFormData({...formData, rating_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="age_banded">Age Banded</SelectItem>
                    <SelectItem value="age_gender">Age + Gender</SelectItem>
                    <SelectItem value="geographic">Geographic</SelectItem>
                    <SelectItem value="tobacco">Tobacco Rated</SelectItem>
                    <SelectItem value="flat">Flat Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Table Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., 2025 GAP Age-Banded Rates"
              />
            </div>

            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData({...formData, effective_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Rate Structure</Label>
                <Button variant="outline" size="sm" onClick={loadTemplate}>
                  Load Template
                </Button>
              </div>

              {formData.rating_method === 'age_banded' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">Age-banded rates (key: max age, value: rate)</p>
                  {Object.entries(formData.rates).map(([age, rate]) => (
                    <div key={age} className="flex gap-2 items-center">
                      <Input
                        type="number"
                        value={age}
                        onChange={(e) => {
                          const newRates = {...formData.rates};
                          delete newRates[age];
                          newRates[e.target.value] = rate;
                          setFormData({...formData, rates: newRates});
                        }}
                        placeholder="Age"
                        className="w-24"
                      />
                      <span className="text-slate-500">â†’</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={rate}
                        onChange={(e) => setFormData({...formData, rates: {...formData.rates, [age]: parseFloat(e.target.value)}})}
                        placeholder="Rate"
                        className="flex-1"
                      />
                      <Button variant="ghost" size="sm" onClick={() => {
                        const newRates = {...formData.rates};
                        delete newRates[age];
                        setFormData({...formData, rates: newRates});
                      }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setFormData({...formData, rates: {...formData.rates, '39': 0}})}>
                    + Add Age Band
                  </Button>
                </div>
              )}

              {formData.rating_method === 'age_gender' && (
                <div className="space-y-4">
                  {['M', 'F'].map(gender => (
                    <div key={gender} className="space-y-2">
                      <Label>{gender === 'M' ? 'Male' : 'Female'} Rates</Label>
                      {Object.entries(formData.rates[gender] || {}).map(([age, rate]) => (
                        <div key={age} className="flex gap-2 items-center">
                          <Input type="number" value={age} className="w-24" disabled />
                          <span>â†’</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={rate}
                            onChange={(e) => setFormData({
                              ...formData,
                              rates: {
                                ...formData.rates,
                                [gender]: {...formData.rates[gender], [age]: parseFloat(e.target.value)}
                              }
                            })}
                            className="flex-1"
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {(formData.rating_method === 'geographic' || formData.rating_method === 'flat') && (
                <div className="space-y-2">
                  {Object.entries(formData.rates).map(([key, value]) => (
                    <div key={key} className="flex gap-2 items-center">
                      <Input
                        value={key}
                        onChange={(e) => {
                          const newRates = {...formData.rates};
                          delete newRates[key];
                          newRates[e.target.value] = value;
                          setFormData({...formData, rates: newRates});
                        }}
                        placeholder="Key"
                        className="flex-1"
                      />
                      <span>â†’</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={(e) => setFormData({...formData, rates: {...formData.rates, [key]: parseFloat(e.target.value)}})}
                        placeholder="Value"
                        className="flex-1"
                      />
                      <Button variant="ghost" size="sm" onClick={() => {
                        const newRates = {...formData.rates};
                        delete newRates[key];
                        setFormData({...formData, rates: newRates});
                      }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setFormData({...formData, rates: {...formData.rates, 'new_key': 0}})}>
                    + Add Entry
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about this rating table..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}